/**
 * Stardrop evolution chain — celestial confections.
 * Completely original; not fruit-themed.
 */

export interface TierDef {
  id: number;
  name: string;
  /** Short flavor line for tutorial / reveal */
  flavor: string;
  /** Primary hex color */
  color: number;
  /** Secondary / accent */
  accent: number;
  /** Emissive tint */
  emissive: number;
  emissiveIntensity: number;
  roughness: number;
  metalness: number;
  /** 0–1 subsurface-ish sheen via transmission-ish color mix */
  softness: number;
  /** Face style key */
  face: 'spark' | 'wisp' | 'puff' | 'orb' | 'moon' | 'macaron' | 'planet' | 'star' | 'comet' | 'truffle' | 'cosmic';
  /** Geometry builder key */
  shape: 'spark' | 'blob' | 'cloud' | 'orb' | 'crescent' | 'disc' | 'ring' | 'star' | 'swirl' | 'gem' | 'cake';
  /** Particle color on merge */
  particleColor: number;
  /** Idle animation amplitude */
  idleBob: number;
  idleSpin: number;
  /** Pitch offset for voice/sfx */
  voicePitch: number;
}

export const TIERS: readonly TierDef[] = [
  {
    id: 0,
    name: 'Sparklebit',
    flavor: 'A sugar-star that just learned to glow.',
    color: 0xfff4b0,
    accent: 0xffe066,
    emissive: 0xfff0a0,
    emissiveIntensity: 0.45,
    roughness: 0.35,
    metalness: 0.15,
    softness: 0.4,
    face: 'spark',
    shape: 'spark',
    particleColor: 0xfff6c8,
    idleBob: 0.04,
    idleSpin: 1.2,
    voicePitch: 1.4,
  },
  {
    id: 1,
    name: 'Jellywisp',
    flavor: 'Soft nebula jelly with a shy smile.',
    color: 0xa8f0ff,
    accent: 0x6edfff,
    emissive: 0x4ecfff,
    emissiveIntensity: 0.25,
    roughness: 0.18,
    metalness: 0.05,
    softness: 0.9,
    face: 'wisp',
    shape: 'blob',
    particleColor: 0xb8f4ff,
    idleBob: 0.06,
    idleSpin: 0.4,
    voicePitch: 1.25,
  },
  {
    id: 2,
    name: 'Cloudpuff',
    flavor: 'A marshmallow cloud that dreams of rain.',
    color: 0xffe0f2,
    accent: 0xffb8e0,
    emissive: 0xffc8e8,
    emissiveIntensity: 0.12,
    roughness: 0.75,
    metalness: 0.0,
    softness: 1.0,
    face: 'puff',
    shape: 'cloud',
    particleColor: 0xffd6ee,
    idleBob: 0.08,
    idleSpin: 0.25,
    voicePitch: 1.15,
  },
  {
    id: 3,
    name: 'Honeyorb',
    flavor: 'Liquid amber spun into a perfect drop.',
    color: 0xffb347,
    accent: 0xff8c1a,
    emissive: 0xff9a2e,
    emissiveIntensity: 0.2,
    roughness: 0.22,
    metalness: 0.25,
    softness: 0.55,
    face: 'orb',
    shape: 'orb',
    particleColor: 0xffc56e,
    idleBob: 0.035,
    idleSpin: 0.55,
    voicePitch: 1.05,
  },
  {
    id: 4,
    name: 'Crescookie',
    flavor: 'A buttery moon with crater freckles.',
    color: 0xe8c49a,
    accent: 0xc9956a,
    emissive: 0xd4a574,
    emissiveIntensity: 0.08,
    roughness: 0.65,
    metalness: 0.05,
    softness: 0.3,
    face: 'moon',
    shape: 'crescent',
    particleColor: 0xf0d4b0,
    idleBob: 0.03,
    idleSpin: 0.35,
    voicePitch: 0.98,
  },
  {
    id: 5,
    name: 'Macaronix',
    flavor: 'Twin shells sandwiching stardust cream.',
    color: 0xc9a0ff,
    accent: 0xff9ed5,
    emissive: 0xb88cff,
    emissiveIntensity: 0.15,
    roughness: 0.4,
    metalness: 0.1,
    softness: 0.5,
    face: 'macaron',
    shape: 'disc',
    particleColor: 0xe0c0ff,
    idleBob: 0.04,
    idleSpin: 0.7,
    voicePitch: 0.92,
  },
  {
    id: 6,
    name: 'Ringpudding',
    flavor: 'Wobbly custard world with a sugar ring.',
    color: 0xffd966,
    accent: 0xff8fab,
    emissive: 0xffc94d,
    emissiveIntensity: 0.18,
    roughness: 0.28,
    metalness: 0.08,
    softness: 0.75,
    face: 'planet',
    shape: 'ring',
    particleColor: 0xffe699,
    idleBob: 0.05,
    idleSpin: 0.5,
    voicePitch: 0.85,
  },
  {
    id: 7,
    name: 'Starflake',
    flavor: 'Frosted star cake, five points of joy.',
    color: 0x7ec8ff,
    accent: 0xffffff,
    emissive: 0x5ab0ff,
    emissiveIntensity: 0.3,
    roughness: 0.32,
    metalness: 0.2,
    softness: 0.4,
    face: 'star',
    shape: 'star',
    particleColor: 0xa8dcff,
    idleBob: 0.045,
    idleSpin: 0.9,
    voicePitch: 0.78,
  },
  {
    id: 8,
    name: 'Sundaewisp',
    flavor: 'A soft-serve comet trailing sprinkles.',
    color: 0xff8ec8,
    accent: 0x9bffb0,
    emissive: 0xff6eb4,
    emissiveIntensity: 0.28,
    roughness: 0.38,
    metalness: 0.05,
    softness: 0.7,
    face: 'comet',
    shape: 'swirl',
    particleColor: 0xffb0d8,
    idleBob: 0.055,
    idleSpin: 0.6,
    voicePitch: 0.7,
  },
  {
    id: 9,
    name: 'Galaxtruffle',
    flavor: 'Dark chocolate nebula with violet galaxies.',
    color: 0x4a2c6a,
    accent: 0xc77dff,
    emissive: 0x8b5cf6,
    emissiveIntensity: 0.4,
    roughness: 0.45,
    metalness: 0.35,
    softness: 0.25,
    face: 'truffle',
    shape: 'gem',
    particleColor: 0xb794f6,
    idleBob: 0.03,
    idleSpin: 0.4,
    voicePitch: 0.6,
  },
  {
    id: 10,
    name: 'Aethercake',
    flavor: 'The final celestial confection. Legend.',
    color: 0xffd6f5,
    accent: 0xffe29a,
    emissive: 0xff9ecd,
    emissiveIntensity: 0.55,
    roughness: 0.3,
    metalness: 0.15,
    softness: 0.6,
    face: 'cosmic',
    shape: 'cake',
    particleColor: 0xffe0f5,
    idleBob: 0.04,
    idleSpin: 0.35,
    voicePitch: 0.5,
  },
] as const;

export function tierRadius(tier: number): number {
  // Non-linear growth — larger tiers dominate space (tuned for jar fill tension)
  const base = 0.46;
  const r = base * Math.pow(1.255, tier);
  return Math.min(r, 2.35);
}

export function tierMass(tier: number): number {
  const r = tierRadius(tier);
  const volume = (4 / 3) * Math.PI * r * r * r;
  // Slight mass dampening so huge pieces aren't immovable tanks
  return volume * (1.0 - tier * 0.02);
}

export function getTier(id: number): TierDef {
  return TIERS[Math.max(0, Math.min(TIERS.length - 1, id))]!;
}
